import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { RouteComponentProps, withRouter } from 'react-router'
import { useFaq } from 'content/api'
import { accountAccessShowLogin } from 'actions/UiActions'
import { fetchEarnOptions, fetchReferralLogs, fetchMyReferralCode } from 'actions/ReferAction'
import { useAppDispatch } from 'hooks/reduxHooks'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import { connect } from 'react-redux'
import zIndex from 'styles/tools/z-index'
import HeadData from 'components/Common/HeadData'
import HeroImage from 'content/components/HeroImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import Group from 'components/utils/Group'
import KeyMessages, { KeyMessage } from 'content/components/KeyMessages'
import PageSection from 'content/components/PageSection'
import LineLinkIcon from 'LuxKit/Icons/line/LineLinkIcon'
import TextLink from 'LuxKit/TextLink'
import FrequentlyAskedQuestions from 'components/Content/FrequentlyAskedQuestions'
import ShareButtons from './ShareButtons'
import Pane from 'components/Common/Pane'
import Icon from 'content/components/Icon'
import ReferralGrid from 'marketing/ReferralGrid'
import ReferralPanel from 'marketing/ReferralPanel'
import TextButton from 'LuxKit/Button/TextButton'
import { addQueryParamsToUrl } from 'lib/url/searchUrlUtils'

const getRewardIcon = (name: string): string => {
  switch (name) {
    case 'vip':
      return 'ticket'
    case 'qff':
      return 'plane-departure'
    default:
      return 'gift'
  }
}

const REFEREE_LEGAL_PAGE_SLUG = 'referral-program-faq'

const CodeBox = styled(Group)`
  max-width: ${rem(600)};
  padding: ${rem(12)} ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background: ${props => props.theme.palette.neutral.default.eight};
  user-select: none;
`

const StyledHeroImage = styled(HeroImage)`
    padding-top: 0;
    padding-bottom: 0;
    padding-right: 0;
    padding-left: 0;

    ${mediaQueryUp.desktop} {
      padding: ${rem(40)} ${rem(24)};
    }
`

const ReferralCodeInfo = styled(VerticalSpacer)`
  padding: ${rem(32)} ${rem(20)} 0 ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(32)} ${rem(20)};

    &.logged-in {
      padding: 0;
    }
  }
`

const Wrapper = styled.div`
  z-index: ${zIndex.min};
  width: 100%;

  ${mediaQueryUp.desktop} {
    max-width: ${rem(1140)};
    display: flex;
    justify-content: space-between;
    gap: ${rem(20)};
  }
`

const CenteredVerticalSpacer = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  align-items: center;

  &.logged-in{
    margin-top: ${rem(48)};
    padding-top: ${rem(64)};
    border-top: 1px solid ${props => props.theme.palette.neutral.default.seven};
  }

  ${mediaQueryUp.desktop} {
    &.logged-in{
      margin-top: ${rem(64)};
    }
  }
`

const RewardGrid = styled.div`
  & > * + * {
    margin-top: ${rem(8)};
  }

  & > * {
    padding: ${rem(20)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 100%;
    align-items: start;
    
    width: 100%;
    gap: ${rem(8)};

    & > * + * {
      margin-top: auto;
    }

    & > * {
      padding: ${rem(28)} ${rem(20)};
      height: 100%;
      justify-content: start;
    }
  }
`

interface RouteProps { }

interface Props extends RouteComponentProps<RouteProps> {
  memberId: string
  earnOptions: Array<App.EarnOption>
  referralLog: App.ReferralLogs
  regionCode: string
  isStoreMode: boolean
  referralCode?: string
  referralUtm?: { [param: string]: string }
}

function RefereeLanding(props: Props) {
  const { memberId, earnOptions, referralLog, regionCode, referralCode, referralUtm, isStoreMode } = props
  const { data: faq } = useFaq(REFEREE_LEGAL_PAGE_SLUG)
  const showReferralPanel = !!referralLog
  const showReferralGrid = showReferralPanel && referralLog.referral_logs.length > 0
  const [hasCopied, setCopied] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const howItWorks: Array<KeyMessage> = useMemo(() => [{
    icon: 'user-plus',
    title: intl.formatMessage({
      id: 'MfqUQ20K9w',
      defaultMessage: 'Refer a friend',
      description: 'How it works step 1 title on referral page',
    }),
    text: intl.formatMessage({
      id: 'sBolEoYBHU',
      defaultMessage: 'Share your unique promo code with your friends.',
      description: 'How it works step 1 description on referral page',
    }),
  }, {
    icon: 'mountains-sun',
    title: intl.formatMessage({
      id: 'ElWU+WqS4m',
      defaultMessage: 'Your friend books their first trip',
      description: 'How it works step 2 title on referral page',
    }),
    text: intl.formatMessage({
      id: 'f7JMjuo1hz',
      defaultMessage: 'Using your unique referral promo code at checkout.',
      description: 'How it works step 2 description on referral page',
    }),
  }, {
    icon: 'gift',
    title: intl.formatMessage({
      id: 'cIqU3rCbgT',
      defaultMessage: 'Earn your reward',
      description: 'How it works step 3 title on referral page',
    }),
    text: intl.formatMessage({
      id: 'tlj/oDoA9x',
      defaultMessage: 'Receive an epic referral reward and start planning your next trip.',
      description: 'How it works step 3 description on referral page',
    }),
  }], [intl])

  const copiedLabel = intl.formatMessage({
    id: 'jvfbmQv4Nm',
    defaultMessage: 'Copied!',
    description: 'Button text shown after referral link is copied to clipboard',
  })
  const copyLabel = intl.formatMessage({
    id: '/Zx8IvPgeh',
    defaultMessage: 'Copy',
    description: 'Button text to copy referral link to clipboard',
  })
  const chooseRewardsHeading = intl.formatMessage({
    id: 'zf+RIZSKQl',
    defaultMessage: 'Choose from a range of rewards',
    description: 'Heading when multiple referral reward options are available',
  })
  const earnRewardHeading = intl.formatMessage({
    id: 'gDjdUXkr7o',
    defaultMessage: 'Earn your reward',
    description: 'Heading when only one referral reward option is available',
  })

  const onShowLogin = () => {
    dispatch(accountAccessShowLogin())
  }

  useEffect(() => {
    dispatch(fetchEarnOptions(regionCode))
  }, [regionCode, dispatch])

  useEffect(() => {
    if (memberId) {
      dispatch(fetchReferralLogs())
      dispatch(fetchMyReferralCode(memberId))
    }
  }, [dispatch, memberId])

  if (memberId && !referralCode) {
    <Error404PageWithOffers />
  }

  const utm = {
    ...referralUtm,
    utm_source: config.BRAND,
    utm_medium: 'referral',
    utm_content: `referrer:${memberId}`,
  }
  const referralLinkWithUtm = addQueryParamsToUrl(`${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/refer/${referralCode}`, utm)
  const referralLink = `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/refer/${referralCode}`

  return (
    <>
      <HeadData
        title="Save money on your first Luxury Escape"
        description="Let Luxury Escapes open up a world of amazing destinations, incredible inclusions and unforgettable experiences"
        imageId="nyaamiuv8bnw1ww4avza2xqifyle"
      />
      <StyledHeroImage
        alt="Beautiful places to visit"
        aria-hidden="true"
        id="mv5u3lasw96k155tldkg"
      >
        <Wrapper>
          <ReferralCodeInfo gap={32} className={clsx(!!memberId && 'logged-in')}>
            <VerticalSpacer gap={12}>
              <Heading variant="heading2" as="h1" colour="neutral-eight" align={!memberId ? 'center' : 'start'}>
                {intl.formatMessage({
                  id: 'ocpmHWdRn5',
                  defaultMessage: 'Refer. Reward. Repeat.',
                  description: 'Main heading on the referral page',
                })}
              </Heading>
              <BodyText variant="large" colour="neutral-eight" align={!memberId ? 'center' : 'start'}>
                {intl.formatMessage(
                  {
                    id: 'lmP3dBED3j',
                    defaultMessage: 'Unlock the ultimate perks for your next escape when you invite your friends to {brandName}. Once they book their first trip, you’ll receive your choice of the below rewards.',
                    description: 'Subheading describing the referral reward program on the referral page',
                  },
                  { brandName: 'Luxury Escapes' },
                )}
              </BodyText>
            </VerticalSpacer>
            {isStoreMode && (
              <BodyText variant="large" colour="neutral-eight" align="start">
                {intl.formatMessage({
                  id: '9+glkITB7M',
                  defaultMessage: 'Speak to our friendly store staff for more information.',
                  description: 'In-store mode message directing users to staff on referral page',
                })}
              </BodyText>
            )}
            {!memberId && !isStoreMode &&
              <CenteredVerticalSpacer gap={32}>
                <TextButton kind="primary" onClick={onShowLogin}>
                  {intl.formatMessage({
                    id: 'DPniMT/NfJ',
                    defaultMessage: 'Log in to continue',
                    description: 'Button text prompting unauthenticated users to log in on referral page',
                  })}
                </TextButton>
              </CenteredVerticalSpacer>}
            {memberId && !isStoreMode && (
              <VerticalSpacer gap={16}>
                <Heading variant="heading6" colour="neutral-eight">
                  {intl.formatMessage({
                    id: 'moonlW4F07',
                    defaultMessage: 'Share my invite link with friends',
                    description: 'Heading for the referral link sharing section',
                  })}
                </Heading>
                <CodeBox direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                  <BodyText variant="medium" wrap="truncate" data-testid="referral-code">
                    {referralLink}
                  </BodyText>
                  <TextButton
                    kind="tertiary"
                    size="large"
                    data-testid="promo-button"
                    startIcon={!hasCopied ? <LineLinkIcon size="S" /> : undefined}
                    onClick={() => {
                      copyValueToClipboard(referralLinkWithUtm)
                      setCopied(true)
                      window.setTimeout(() => {
                        setCopied(false)
                      }, 1500)
                    }}
                  >
                    {hasCopied ? copiedLabel : copyLabel}
                  </TextButton>
                </CodeBox>
                <VerticalSpacer gap={8}>
                  <BodyText variant="large" colour="neutral-eight">
                    {intl.formatMessage({
                      id: 'iWOusUXK46',
                      defaultMessage: 'Or share via:',
                      description: 'Label above social share buttons on referral page',
                    })}
                  </BodyText>
                  <ShareButtons code={referralCode!} utm={utm} />
                </VerticalSpacer>
              </VerticalSpacer>
            )}
          </ReferralCodeInfo>
          {memberId && showReferralPanel && <ReferralPanel referralLogTotal={referralLog.totals} />}
        </Wrapper>
      </StyledHeroImage>
      <PageSection>
        {memberId && showReferralGrid && <ReferralGrid referralLogs={referralLog.referral_logs} />}
        <CenteredVerticalSpacer className={clsx((!!memberId && showReferralGrid) && 'logged-in')} gap={16}>
          <Heading variant="heading3" align="center">
            {intl.formatMessage({
              id: 'NvXhvi3QtK',
              defaultMessage: 'How it works',
              description: 'Heading for the how-it-works section on referral page',
            })}
          </Heading>
          <BodyText variant="large" align="center">
            {intl.formatMessage(
              {
                id: 'dpKAQttfzY',
                defaultMessage: 'Referring friends to {brandName} is now easier and more rewarding than ever.',
                description: 'Description text in the how-it-works section on referral page',
              },
              { brandName: 'Luxury Escapes' },
            )}
          </BodyText>
          <KeyMessages items={howItWorks} />
          <BodyText variant="medium">
            <FormattedMessage
              id="+CaRWLYzmi"
              defaultMessage="Read our full <link>Terms & Conditions</link>"
              description="Link to terms and conditions on referral page"
              values={{
                link: (chunks) => <TextLink weight="regular" to="/terms-and-conditions">{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </CenteredVerticalSpacer>
      </PageSection>

      {earnOptions && (
        <PageSection>
          <CenteredVerticalSpacer gap={16}>
            <Heading variant="heading3">
              {earnOptions.length > 1 && chooseRewardsHeading}
              {earnOptions.length === 1 && earnRewardHeading}
            </Heading>
            <RewardGrid>
              {earnOptions.map((option, index) => <Pane key={index}>
                <Icon name={getRewardIcon(option.earn_type_code)} size={24} type="primary" />
                <BodyText weight="bold" variant="large" align="center">{option.display_name}</BodyText>
              </Pane>)}
            </RewardGrid>
          </CenteredVerticalSpacer>
        </PageSection>
      )}

      {faq && (
        <PageSection>
          <FrequentlyAskedQuestions
            heading={intl.formatMessage({
              id: 'MkqsvLOnZK',
              defaultMessage: 'Frequently asked questions',
              description: 'Heading for the FAQ section on referral page',
            })}
            items={faq}
            maxToShow={10}
          />
        </PageSection>
      )}
    </>
  )
}

const mapState = (state: App.State) => {
  return {
    memberId: state.auth.account.memberId,
    regionCode: state.geo.currentRegionCode,
    earnOptions: state.refer.earnOptions,
    referralLog: state.refer.referralLogs,
    referralCode: state.refer.referralCode,
    referralUtm: state.refer.utm,
    isStoreMode: state.system.storeMode,
  }
}

const mapDispatchToProps = {
  fetchEarnOptions,
  fetchReferralLogs,
  fetchMyReferralCode,
}

export default withRouter(connect(mapState, mapDispatchToProps)(RefereeLanding))
